if (!customElements.get('product-editorial-gallery')) {
  customElements.define(
    'product-editorial-gallery',
    class ProductEditorialGallery extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('[data-editorial-gallery-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-editorial-gallery-thumb]'));
        this.thumbTrack = this.querySelector('[data-editorial-gallery-thumbs]');
        this.variants = this.parseVariants();
        this.currentIndex = Math.max(
          0,
          this.slides.findIndex((slide) => slide.classList.contains('is-active'))
        );

        this.addEventListener('click', this.handleClick.bind(this));
        this.addEventListener('keydown', this.handleKeydown.bind(this));
        this.variantChangeUnsubscriber =
          typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
            ? subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this))
            : null;
        this.setActive(this.currentIndex, false);
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      parseVariants() {
        try {
          return JSON.parse(this.querySelector('[data-editorial-variants-json]')?.textContent || '[]');
        } catch {
          return [];
        }
      }

      handleClick(event) {
        const previousButton = event.target.closest('[data-editorial-gallery-prev]');
        const nextButton = event.target.closest('[data-editorial-gallery-next]');
        const thumb = event.target.closest('[data-editorial-gallery-thumb]');

        if (previousButton) {
          this.previous();
          return;
        }

        if (nextButton) {
          this.next();
          return;
        }

        if (thumb) {
          this.setActive(Number(thumb.dataset.index));
        }
      }

      handleKeydown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.previous();
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next();
        }
      }

      previous() {
        this.setActive(this.currentIndex - 1);
      }

      next() {
        this.setActive(this.currentIndex + 1);
      }

      setActive(index, scrollThumb = true, syncVariant = true) {
        if (this.slides.length === 0) return;

        const activeIndex = (index + this.slides.length) % this.slides.length;
        this.currentIndex = activeIndex;

        this.slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === activeIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        this.thumbs.forEach((thumb, thumbIndex) => {
          const isActive = thumbIndex === activeIndex;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        if (scrollThumb) {
          this.thumbs[activeIndex]?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }

        if (syncVariant) {
          this.selectVariantForSlide(this.slides[activeIndex]);
        }
      }

      handleVariantChange({ data } = {}) {
        const productInfo = this.closest('product-info');
        if (!productInfo || !data?.variant || data.sectionId !== productInfo.dataset.section) return;

        const mediaId = data.variant.featured_media?.id;
        if (!mediaId) return;

        const mediaIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (mediaIndex >= 0 && mediaIndex !== this.currentIndex) {
          this.setActive(mediaIndex, true, false);
        }
      }

      selectVariantForSlide(slide) {
        const mediaId = slide?.dataset.mediaId;
        if (!mediaId || this.variants.length === 0) return;

        const variant = this.variants.find((item) => item.featured_media_id === String(mediaId));
        if (!variant) return;

        const productInfo = this.closest('product-info');
        const currentVariantInput = productInfo?.querySelector(
          `#product-form-${productInfo.dataset.section} input[name="id"]`
        );

        this.updateVariantInputs(productInfo, variant.id);

        if (currentVariantInput?.value === String(variant.id)) return;

        this.selectVariantOptions(variant);
      }

      updateVariantInputs(productInfo, variantId) {
        if (!productInfo || !variantId) return;

        productInfo
          .querySelectorAll(
            `#product-form-${productInfo.dataset.section}, #product-form-installment-${productInfo.dataset.section}`
          )
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            if (!input || input.value === String(variantId)) return;
            input.value = variantId;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      selectVariantOptions(variant) {
        const productInfo = this.closest('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects');
        if (!variantSelects || !Array.isArray(variant.options)) return;

        const optionGroups = Array.from(variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown'));
        let changedTarget = null;

        variant.options.forEach((optionValue, index) => {
          const group = optionGroups[index];
          if (!group) return;

          const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
            (input) => input.value === optionValue
          );

          if (radio && !radio.checked) {
            radio.checked = true;
            changedTarget = radio;
            return;
          }

          const select = group.querySelector('select');
          if (select && select.value !== optionValue) {
            select.value = optionValue;
            changedTarget = select;
          }
        });

        changedTarget?.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  );
}

const EDITORIAL_HASH_ALIASES = {
  EditorialFrame: 'Frame',
  EditorialDetails: 'Details',
  EditorialPackaging: 'Packaging',
  EditorialCare: 'Care',
  EditorialCraftsmanship: 'Craftsmanship',
};

const getEditorialTargetId = (hash) => {
  const hashId = hash?.replace(/^#/, '');
  if (!hashId) return '';
  if (document.getElementById(hashId)) return hashId;

  const legacyKey = Object.keys(EDITORIAL_HASH_ALIASES).find((key) => hashId === key || hashId.startsWith(`${key}-`));
  return legacyKey ? EDITORIAL_HASH_ALIASES[legacyKey] : hashId;
};

const getEditorialNavTarget = (link) => document.getElementById(getEditorialTargetId(link.hash));

const setActiveEditorialNavLink = (link) => {
  const nav = link?.closest('.product-editorial__section-nav');
  if (!nav) return;

  nav.querySelectorAll('a[href^="#"]').forEach((navLink) => {
    const isActive = navLink === link;
    navLink.classList.toggle('is-active', isActive);

    if (isActive) {
      navLink.setAttribute('aria-current', 'true');
    } else {
      navLink.removeAttribute('aria-current');
    }
  });
};

const getEditorialScrollOffset = () => {
  const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
  return (Number.isNaN(headerHeight) ? 70 : headerHeight) + 24;
};

const setupEditorialScrollSpy = () => {
  const navs = Array.from(document.querySelectorAll('.product-editorial__section-nav'));
  const groups = navs
    .map((nav) => ({
      items: Array.from(nav.querySelectorAll('a[href^="#"]'))
        .map((link) => ({ link, target: getEditorialNavTarget(link) }))
        .filter((item) => item.target),
    }))
    .filter((group) => group.items.length > 0);

  if (groups.length === 0) return;

  let frameRequested = false;

  const update = () => {
    frameRequested = false;
    const offset = getEditorialScrollOffset();
    const probe = offset + Math.min(window.innerHeight * 0.28, 180);

    groups.forEach(({ items }) => {
      let activeItem = items[0];

      items.forEach((item) => {
        if (item.target.getBoundingClientRect().top <= probe) {
          activeItem = item;
        }
      });

      setActiveEditorialNavLink(activeItem.link);
    });
  };

  const requestUpdate = () => {
    if (frameRequested) return;
    frameRequested = true;
    window.requestAnimationFrame(update);
  };

  update();
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate);
  window.addEventListener('hashchange', requestUpdate);
};

setupEditorialScrollSpy();

const normalizeLegacyEditorialHash = () => {
  const targetId = getEditorialTargetId(window.location.hash);
  if (!targetId || window.location.hash === `#${targetId}`) return;

  const target = document.getElementById(targetId);
  if (!target) return;

  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${targetId}`);
  target.scrollIntoView({ block: 'start' });
};

normalizeLegacyEditorialHash();

document.addEventListener('click', (event) => {
  const clickedElement = event.target instanceof Element ? event.target : null;
  const link = clickedElement?.closest('.product-editorial__section-nav a[href^="#"]');
  if (!link) return;

  const target = getEditorialNavTarget(link);
  if (!target) return;

  event.preventDefault();

  setActiveEditorialNavLink(link);

  target.scrollIntoView({
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    block: 'start',
  });

  window.history.pushState(null, '', link.hash);
});
